import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Snackbar,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SmsIcon from "@mui/icons-material/Sms";
import { useTranslation } from "../i18n";
import { formatInteger } from "../utils/formatters";

interface ShareProgramDialogProps {
  open: boolean;
  onClose: () => void;
  shareToken: string;
  companyName: string;
  totalWeight: number;
  lot?: string;
}

export default function ShareProgramDialog({
  open,
  onClose,
  shareToken,
  companyName,
  totalWeight,
  lot,
}: ShareProgramDialogProps) {
  const { t } = useTranslation();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const frontendUrl = window.location.origin;
  const shareUrl = `${frontendUrl}/p/${shareToken}`;
  const smsMessage = `${companyName}'s program${lot ? ` (${lot})` : ''} for Devi Impex weighing ${formatInteger(totalWeight)} kg can be tracked here: ${shareUrl}. Thank you.`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setSnackbarMessage("Link copied to clipboard!");
    setSnackbarOpen(true);
  };

  const handleCopySms = () => {
    navigator.clipboard.writeText(smsMessage);
    setSnackbarMessage("SMS message copied to clipboard!");
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            m: { xs: 2, sm: 3 },
            maxWidth: { xs: "calc(100% - 32px)", sm: 600 },
            width: "100%",
            borderRadius: { xs: 2, sm: 2 },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: { xs: "1rem", sm: "1.25rem" },
            px: { xs: 2, sm: 3 },
            pt: { xs: 2, sm: 3 },
            pb: { xs: 1, sm: 2 },
            fontWeight: 600,
          }}
        >
          {t("programs.shareProgram")}
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: { xs: 2, sm: 2.5 },
              fontSize: { xs: "0.813rem", sm: "0.875rem" },
              lineHeight: { xs: 1.5, sm: 1.6 },
            }}
          >
            Share this link with your customer via SMS or any messaging app.
            They can view the program status without logging in.
          </Typography>

          {/* SMS Message Section */}
          <Typography
            variant="subtitle2"
            sx={{
              mb: 1,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <SmsIcon fontSize="small" color="primary" />
            {t("programs.shareViaSms")}
          </Typography>
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              bgcolor: "primary.50",
              borderRadius: 1,
              display: "flex",
              flexDirection: "column",
              gap: { xs: 1.5, sm: 1 },
              border: "1px solid",
              borderColor: "primary.200",
              mb: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: "0.813rem", sm: "0.875rem" },
                lineHeight: 1.6,
                color: "text.primary",
              }}
            >
              {smsMessage}
            </Typography>
            <Button
              onClick={handleCopySms}
              variant="contained"
              size="medium"
              startIcon={<SmsIcon />}
              fullWidth
              sx={{
                minWidth: "auto",
                whiteSpace: "nowrap",
              }}
            >
              Copy SMS Message
            </Button>
          </Box>

          {/* Link Only Section */}
          <Typography
            variant="subtitle2"
            sx={{
              mb: 1,
              fontWeight: 600,
            }}
          >
            Link Only
          </Typography>
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              bgcolor: "grey.100",
              borderRadius: 1,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              gap: { xs: 1.5, sm: 1 },
              border: "1px solid",
              borderColor: "grey.300",
            }}
          >
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  wordBreak: "break-all",
                  fontFamily: "monospace",
                  fontSize: { xs: "0.688rem", sm: "0.813rem" },
                  overflowWrap: "anywhere",
                  lineHeight: 1.5,
                  color: "text.primary",
                }}
              >
                {shareUrl}
              </Typography>
            </Box>
            <Button
              onClick={handleCopyLink}
              variant="outlined"
              size="medium"
              startIcon={<ContentCopyIcon />}
              sx={{
                display: { xs: "flex", sm: "none" },
                minWidth: "auto",
                whiteSpace: "nowrap",
              }}
            >
              Copy Link
            </Button>
            <IconButton
              onClick={handleCopyLink}
              color="primary"
              size="small"
              title="Copy to clipboard"
              sx={{
                display: { xs: "none", sm: "inline-flex" },
                flexShrink: 0,
              }}
            >
              <ContentCopyIcon />
            </IconButton>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            pb: { xs: 2, sm: 2.5 },
            pt: { xs: 1, sm: 1 },
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            fullWidth
            sx={{ display: { xs: "block", sm: "none" } }}
          >
            Close
          </Button>
          <Button
            onClick={onClose}
            sx={{ display: { xs: "none", sm: "inline-flex" } }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </>
  );
}

